// GOOGLE TAG MANAGER HELPERS

import {canViewTestCode} from '@/js/global/config.js';


// GTM EVENTS - BROWSING MENUS or its ITEMS
// ================================================================================================

/**
 * The GTM event for when a list of menus is viewed by the customer or a list of a menu's items
 * for them to select from.
 *
 * @type {string}
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_item_list
 */
export const IMPRESSION_EVENT_VIEW_ITEM_LIST = 'view_item_list';

/**
 * The GTM event for when the customer views a menu's details page or a menu item's details
 * (currently displayed in a modal).
 *
 * @type {string}
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#view_item
 */
export const IMPRESSION_EVENT_VIEW_ITEM_DETAILS = 'view_item';

/**
 * The GTM event when the customer clicks on a menu from a list of menus or any of its items
 * from the menu's details page.
 *
 * @type {string}
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#select_item
 */
export const ACTION_EVENT_SELECT_ITEM = 'select_item';



// GTM EVENTS - INTERACTING WITH THE CART
// ================================================================================================

/**
 * The GTM event when the customer adds an item to their cart.
 *
 * @type {string}
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_to_cart
 */
export const ACTION_EVENT_ADD_TO_CART = 'add_to_cart';

/**
 * The GTM event when the customer removes an item from their cart.
 *
 * @type {string}
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#remove_from_cart
 */
export const ACTION_EVENT_REMOVE_FROM_CART = 'remove_from_cart';



// GTM EVENTS - CHECKOUT
// ================================================================================================

/**
 * The GTM event when the customer begins the checkout process.
 *
 * @type {string}
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#begin_checkout
 */
export const ACTION_EVENT_BEGIN_CHECKOUT = 'begin_checkout';

/**
 * The GTM event when the customer adds information about their shipping address.
 *
 * @type {string}
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_shipping_info
 */
export const ACTION_EVENT_ADD_SHIPPING_INFO = 'add_shipping_info';

/**
 * The GTM event when the customer adds their payment details (card, paypal, etc).
 *
 * @type {string}
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#add_payment_info
 */
export const ACTION_EVENT_ADD_PAYMENT_INFO = 'add_payment_info';

/**
 * The GTM event when the customer has completed the checkout process.
 *
 * @type {string}
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag#purchase
 */
export const ACTION_EVENT_COMPLETE_PURCHASE = 'purchase';



// HELPERS
// ================================================================================================

/**
 * Check if Google Tag Manager is enabled (needed) for the current page.
 *
 * @return {boolean}
 */
export const isGtmEnabled = () => {
   try {
      return window.mymy.gtm.isEnabled === true;
   }
   catch (e) {
      if(window.Sentry) {
         Sentry.captureException(e);
      }
      console.error(e);
   }

   return false;
};

/**
 * Set extra data to be combined with data we're pushing into the `dataLayer`.
 *
 * Use this when a Vue instance is created(). It gets data about the `user`, `webPage`,
 * `order`, etc that all GTM events will include before getting pushed into the `dataLayer`.
 */
export const setGtmContext = () => {
   if (! isGtmEnabled()) {
      return;
   }

   // keys we want to not include in our gtmContext data because they are too specific
   const gtmContextKeysBlacklist = [
      'event', 'eventLabel', 'eventCategory', 'ecommerce', 'gtm', 'gtm.uniqueEventId'
   ];

   let context = {};
   for (const [key, value] of Object.entries(window.mymy.gtm.context)) {
      if (!gtmContextKeysBlacklist.includes(key)) {
         context[key] = value;
      }
   }

   window.mymy.gtm.context = context;
};

/**
 * Push data to Google Tag Manager's `dataLayer`.
 *
 * Call this method to combine our `gtmContext` and push it to the `dataLayer`.
 *
 * @param data {object}
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#select_an_item_from_a_list
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#clear_the_ecommerce_object
 */
export const pushToDataLayer = data => {
   // add our gtmContext data to data being passed in (if we have set `gtmContext`)
   if (isGtmEnabled() && typeof data === 'object' && data) {
      dataLayer.push({...window.mymy.gtm.context, ...data});
   }
};

/**
 * Push an event into the `dataLayer` concerning e-commerce actions/impressions.
 *
 * @param name {string}
 * @param items {array}
 * @param params {object}
 */
export const pushCommerceEventToDataLayer = (name, items = [], params = {}) => {
   // block the event if GTM is disabled on this environment
   if (! isGtmEnabled() || typeof name !== 'string' || ! name) {
      return;
   }

   /**
    * Clear the previous ecommerce object
    *
    * @see
    * https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#clear_the_ecommerce_object
    */
   try {
      dataLayer.push({ ecommerce: null });
   }
   catch (e) {
      if(window.Sentry) {
         Sentry.captureException(e);
      }
      console.error(e);
   }

   try {
      // add the menu items involved in the event
      params['items'] = items;

      // log the event in the console on our "testing" environments
      if (canViewTestCode()) {
         console.log('GTM event fired:', {event: name, ecommerce: params});
      }

      // push the new ecommerce object
      dataLayer.push({ event: name, ecommerce: params });
   }
   catch (e) {
      if(window.Sentry) {
         Sentry.captureException(e);
      }
      console.error(e);
   }
};

/**
 * Use this to create an 'item' object to add to an `items` array in a Google Tag Manager event.
 *
 * @param item {object}
 * @param category {array}
 * @param list {object}
 * @param custom {object}
 * @return {object|null}
 * @see https://developers.google.com/analytics/devguides/collection/ga4/item-scoped-ecommerce#add_an_item-scoped_custom_parameter
 * @see https://www.simoahava.com/analytics/google-analytics-4-ecommerce-guide-google-tag-manager/#product-data
 */
export const gtmItem = (item, category = [], list = {}, custom = {}) => {
   // integrity check
   if (typeof item !== 'object' || ! item) {
      return null;
   }

   // either item.id...
   if (! (item.id ?? null)) {
      // or item.name is required
      if (! (item.name ?? null)) {
         return null;
      }
   }

   let gtmItemObj = {};

   // item category data
   for (const [index, value] of category.entries()) {
      // google's 'category' properties are not 0-indexed
      let count = index + 1;

      // google doesn't have more than 5 'category' properties
      if (count > 5) {
         break;
      }

      if (typeof value === 'string' && value) {
         gtmItemObj['item_category' + (count > 1 ? count : '')] = value;
      }
   }

   // about the list this item was in and its index in the list
   for (const key in list) {
      if (key === 'index' && typeof list[key] === 'number') {
         gtmItemObj['index'] = list[key];
         continue;
      }

      if (typeof list[key] === 'string' && list[key]) {
         gtmItemObj['item_list_' + key] = list[key];
      }
   }

   // about the item itself
   const gtmParams = {
      item_id:          {type: 'string', itemKey: 'id'},
      item_name:        {type: 'string', itemKey: 'name'},
      affiliation:      {type: 'string', itemKey: 'affiliation'},
      coupon:           {type: 'string', itemKey: 'coupon'},
      discount:         {type: 'number', itemKey: 'discount'},
      item_brand:       {type: 'string', itemKey: 'brand'},
      item_variant:     {type: 'string', itemKey: 'variant'},
      location_id:      {type: 'string', itemKey: 'locationId'},
      price:            {type: 'number', itemKey: 'price'},
      quantity:         {type: 'number', itemKey: 'quantity'},
   };

   for (const key in gtmParams) {
      let param = gtmParams[key];
      let value = item[param.itemKey] ?? null;

      // integrity check
      if (typeof value !== param.type) {
         continue;
      }

      // no empty strings
      if (param.type === 'string' && ! value) {
         continue;
      }

      // add it to the object
      gtmItemObj[key] = value;
   }

   // add in any custom params
   gtmItemObj = {...gtmItemObj, ...custom};

   return Object.keys(gtmItemObj).length ? gtmItemObj : null;
};